import React from 'react';
import TermsLayout from './TermsLayout';

function DataProcessingAgreement() {
  const sections = [
    {
      title: "1. Introduction and Scope",
      content: `This Data Processing Agreement ("DPA") forms part of the Terms of Service between Zella Wellbeing ("Zella") and any organisation ("Customer") that uses the Zella platform to collect employee wellbeing data.

By accepting Zella's Terms of Service, the Customer also accepts this DPA. No separate signature is required.

This DPA is required by Article 28 of UK GDPR and, where applicable, Article 28 of EU GDPR. It sets out the rights and obligations of each party in respect of the processing of personal data by Zella on behalf of the Customer.

Dual Role Clarification:

Zella operates in two distinct capacities:

1. As a Data Processor — in respect of employee survey and account data that the Customer (as data controller) collects through the Zella platform. This DPA governs that relationship.

2. As an Independent Data Controller — in respect of administrator account data (names, emails, billing information) and aggregated research datasets that Zella uses for its own purposes (account management, billing, service improvement, research). This is governed by Zella's Privacy Policy, not this DPA.

This DPA concerns Zella's role as Data Processor only.

Precedence:

In the event of any conflict between this DPA and the Terms of Service on matters of data protection, this DPA shall prevail.`
    },
    {
      title: "2. Definitions",
      content: `"Personal Data" has the meaning given in UK GDPR Article 4(1): any information relating to an identified or identifiable natural person.

"Processing" has the meaning given in UK GDPR Article 4(2): any operation performed on personal data, including collection, storage, use, disclosure, and deletion.

"Data Controller" means the Customer — the organisation that determines the purposes and means of processing employee personal data.

"Data Processor" means Zella — which processes employee personal data on behalf of and under the instructions of the Customer.

"Data Subject" means the individual employee whose personal data is being processed.

"Sub-Processor" means any third party engaged by Zella to process personal data on Zella's behalf in connection with providing the Services.

"Personal Data Breach" has the meaning given in UK GDPR Article 4(12): a breach of security leading to the accidental or unlawful destruction, loss, alteration, unauthorised disclosure of, or access to, personal data transmitted, stored, or otherwise processed.

"UK GDPR" means the UK General Data Protection Regulation as retained in UK law by the European Union (Withdrawal) Act 2018, as amended.

"EU GDPR" means Regulation (EU) 2016/679 of the European Parliament and of the Council.

"Data Protection Legislation" means UK GDPR and the Data Protection Act 2018, and where applicable EU GDPR, and any successor legislation.

"Services" means the Zella workplace wellbeing platform as described in the Terms of Service.

"Supervisory Authority" means the Information Commissioner's Office (ICO) in the UK, or the relevant EU data protection authority for EU Customers.

"Standard Contractual Clauses" or "SCCs" means the standard contractual clauses for the transfer of personal data to third countries approved by the European Commission under EU GDPR.`
    },
    {
      title: "3. Details of Processing — Article 28 Annex",
      content: `The following table summarises the key details required by Article 28(3) of UK GDPR and EU GDPR:

Controller: Customer (the organisation deploying Zella)
Processor: Zella Wellbeing
Purpose: Workplace wellbeing monitoring — enabling the Customer to identify team-level fatigue and wellbeing patterns for occupational health and safety purposes
Data Subjects: Employees of the Customer enrolled in the Zella platform (pending, approved, or rejected)
Personal Data: Employee account data, survey response data, feedback data (see below)
Duration: Subscription term plus deletion/return period as set out in Section 9
Sub-Processors: Firebase / Google Cloud (UK), Stripe (US), Google reCAPTCHA (US), Email Service Provider (see Section 6)

Types of Personal Data Processed:

Employee account data (Firestore users collection and Firebase Authentication):
- Email address
- Display name (optional)
- Password hash (managed by Firebase Authentication — not accessible to Zella)
- Company and team assignment (stored as Firebase-generated identifier codes)
- Account status (pending / approved / rejected) and associated timestamps
- Firebase Authentication custom claims (admin: false, companyId)
- Firebase Authentication metadata (account creation timestamp, last sign-in timestamp)

Employee survey data (Firestore surveys collection):
- User ID (uid) — a Firebase-generated identifier linking to the employee's account record
- Team ID and company ID (Firebase-generated identifier codes, not human-readable names)
- Happiness rating (integer, 1–5)
- Overall tiredness rating (integer, 1–5)
- Reaction time in milliseconds (optional)
- Submission timestamp

Note on pseudonymisation: Survey records contain the employee's uid. This uid links to the account record which contains name and email. Zella's Firestore security rules prevent administrators from reading the surveys collection directly. Survey data is therefore pseudonymised — not anonymised — at the database level.

Aggregated team average data (Firestore teamAverages collection):
- Company ID and team ID (Firebase-generated identifier codes)
- Week start date
- Average happiness, average tiredness, average reaction time
- Survey count
This collection contains no human-readable names, emails, or individual identifiers.

Employee feedback data (Firestore feedback collection):
- User ID (uid) and company ID
- Feedback message or bug report text
- Feedback type and submission timestamp

Rate limiting records (multiple Firestore collections):
- User ID or admin ID used as collection key
- Action count and time bucket
- Automatically deleted after 48 hours

Special Category Data:
Zella is not designed or intended for the collection of special category personal data under Article 9 of UK GDPR. The platform collects only numerical ratings (happiness, tiredness, reaction time). Customers must not intentionally use Zella to collect medical diagnoses, health records, disability information, or other special category data unless separately agreed in writing with Zella.

Data Not Collected:
Zella does not collect location data, precise device identifiers, biometric data, browsing history, health records, or free-text survey responses.`
    },
    {
      title: "4. Customer's Obligations as Data Controller",
      content: `The Customer, as data controller, accepts sole responsibility for:

Lawful Basis for Processing:
Determining, documenting, and relying on a valid lawful basis under UK GDPR Article 6 for all processing of employee personal data through Zella. Appropriate lawful bases may include:
- Article 6(1)(f) — legitimate interests in workplace health and safety monitoring (subject to a legitimate interests assessment)
- Article 6(1)(a) — consent (noting that consent in an employment context may not be freely given and should be assessed carefully)
- Article 6(1)(c) — compliance with a legal obligation (where workplace safety law requires monitoring)

The Customer is solely responsible for determining which lawful basis is appropriate. Zella does not provide legal advice on this question.

Employee Notice and Transparency:
Providing employees with a privacy notice before or at the point of deploying Zella, covering the identity of the data controller, the purposes and lawful basis for processing, the categories of data collected, how data is shared (with Zella as processor, and via team averages with administrators), data subject rights, and retention periods.

Data Subject Rights Facilitation:
Receiving and responding to employee rights requests under Data Protection Legislation. Where Zella can technically assist, the process is set out in Section 7.

Employment Law Compliance:
Ensuring that deployment of Zella complies with applicable employment law, including any obligation to inform and consult employees or employee representatives prior to introducing monitoring systems (Information and Consultation of Employees Regulations 2004, where applicable).

Employment Decision Responsibility:
Zella does not determine employment, disciplinary, compensation, promotion, fitness-for-work, scheduling, or workplace safety decisions. Such decisions remain solely the responsibility of the Customer as data controller. The Customer must not use Zella outputs as the sole or primary basis for any individual employment decision.

Instruction Authority:
Ensuring all instructions to Zella regarding processing are lawful. If the Customer instructs Zella to process data in a manner that would infringe Data Protection Legislation, the Customer accepts sole liability for any resulting harm.

Security Incident Cooperation:
The Customer shall reasonably cooperate with Zella in investigating and remediating any security incident or personal data breach, including providing relevant information about customer-side systems or actions that may have contributed to the incident.

Onward Transfers:
Not exporting personal data from Zella to third countries without ensuring appropriate safeguards are in place.`
    },
    {
      title: "5. Zella's Obligations as Data Processor",
      content: `Zella agrees to comply with the following obligations under Article 28(3) of UK GDPR:

(a) Processing Only on Documented Instructions:

Zella will process personal data only on the documented instructions of the Customer, as set out in this DPA and the Terms of Service, unless required to do so by applicable law. If required by law to process beyond the Customer's instructions, Zella will inform the Customer before doing so unless prohibited by law.

If Zella considers that any instruction infringes Data Protection Legislation, Zella will inform the Customer immediately and may suspend processing of the affected data until the Customer provides clarified or lawful instructions.

(b) Confidentiality:

Zella will ensure that all personnel authorised to process personal data under this DPA are subject to binding confidentiality obligations (whether contractual or statutory). Confidentiality obligations survive termination of this DPA indefinitely.

(c) Security Measures (Article 32):

Zella will implement and maintain appropriate technical and organisational security measures to protect personal data against accidental or unlawful destruction, loss, alteration, unauthorised disclosure, or access.

Current technical measures:
- Encryption in transit: TLS 1.3 (HTTPS) for all data transmission
- Encryption at rest: Provided by Google Cloud and Firebase infrastructure
- Password security: Managed by Firebase Authentication; Zella does not store or have access to plaintext passwords
- Access control: Role-based access using Firebase custom claims (isAdmin flag, companyId)
- Firestore security rules: Formally defined database-layer rules enforcing data access boundaries independently of application logic
- Firebase App Check: Cryptographic attestation that requests originate from genuine Zella app clients
- Rate limiting: Per-user action limits to prevent abuse
- Google reCAPTCHA: Bot prevention at company administrator registration
- PCI DSS compliance: Card data handled exclusively by Stripe (Level 1 PCI DSS certified)

Current organisational measures:
- Access to production data restricted to authorised Zella personnel only
- Data processing agreements in place with all sub-processors
- Incident response procedures for suspected personal data breaches
- Privacy by design — survey data is pseudonymised at collection

Zella will review these measures periodically and update them as required.

(d) Sub-Processors:

Zella will not engage any sub-processor not listed in Section 6 without giving the Customer at least 30 days advance notice and an opportunity to object on reasonable data protection grounds. Zella imposes equivalent data protection obligations on all sub-processors and remains fully liable for their acts and omissions.

(e) Assistance with Data Subject Rights:

Zella will assist the Customer in fulfilling data subject rights requests as set out in Section 7.

(f) Assistance with Article 32–36 Obligations:

Zella will assist the Customer with security obligations (Article 32), breach notification to the Supervisory Authority (Article 33), communication of breaches to data subjects (Article 34), data protection impact assessments (Article 35), and prior consultation (Article 36).

(g) Deletion or Return of Data:

On termination of the Services, Zella will delete or return personal data as set out in Section 9.

(h) Audit and Demonstration of Compliance:

Zella will make available information necessary to demonstrate compliance with this DPA and will cooperate with audits as set out in Section 10, subject to confidentiality, security, and protection of Zella's proprietary information.

Record of Processing Activities:

Zella maintains a record of processing activities under Article 30(2) of UK GDPR, available on request.

Government Data Requests:

If Zella receives a legally binding request from a governmental or regulatory authority for personal data processed under this DPA, Zella will, where legally permitted, notify the Customer before disclosure and give the Customer an opportunity to seek appropriate legal protection.`
    },
    {
      title: "6. Sub-Processors",
      content: `The Customer provides general written authorisation for Zella to engage the following sub-processors:

Firebase / Google Cloud Platform (Google LLC)
Role: Core infrastructure — Firestore database, Firebase Authentication, Cloud Functions, Cloud Storage
Data processed: All employee account data, survey records, team averages, feedback records, rate limiting records, billing metadata
Data location: europe-west2 (London, United Kingdom)
International transfer: Core data remains in the UK. Firebase Authentication and Cloud Functions may involve Google infrastructure globally; Google LLC is certified under the UK-US Data Bridge
Agreement: Google Cloud Data Processing Addendum
Further information: https://cloud.google.com/terms/data-processing-addendum

Stripe, Inc.
Role: Payment card processing only
Data processed: Payment card metadata and billing information. Stripe does not receive any employee account, survey, or feedback data.
Data location: United States
Transfer mechanism: UK-US Data Bridge (Stripe certified)
Agreement: Stripe Data Processing Agreement
Further information: https://stripe.com/gb/privacy

Google LLC (reCAPTCHA)
Role: Bot prevention at company administrator registration only — one-time event, not ongoing
Data processed: Device characteristics, browser type, interaction patterns, IP address at registration step only
Data location: United States
Transfer mechanism: UK-US Data Bridge (Google certified)
Further information: https://policies.google.com/privacy

Email Service Provider
Role: Transactional email delivery (account verification, password resets, billing notifications)
Data processed: Email address and display name
Details: Available on request from privacy@zellawellbeing.com

Changes to Sub-Processors:

Zella will give the Customer at least 30 days written notice before adding or replacing any sub-processor. The Customer may object in writing within 30 days on reasonable data protection grounds. If Zella cannot accommodate a valid objection, the Customer may terminate the affected Services without penalty.`
    },
    {
      title: "7. Data Subject Rights — Assistance",
      content: `The Customer is the primary point of contact for employees exercising rights under Data Protection Legislation. Employees should contact their employer in the first instance.

Where Zella holds relevant data and can technically assist, Zella will do so on written request from the Customer within 30 days.

Right of Access (Article 15):

Zella can provide, on request, in JSON or CSV format:
- Account record: email, name, team and company assignment (as Firebase-generated IDs), account status and timestamps
- Firebase Authentication metadata: account creation date, last sign-in date
- Survey records associated with the employee's uid: happiness, tiredness, reaction time, submission timestamps, team ID, company ID
- Feedback records associated with the employee's uid

Note: Team averages (teamAverages collection) contain no individual identifiers and are not personal data for the purposes of a subject access request.

Right to Erasure (Article 17):

Standard erasure on account deletion:
- All account data (users collection, Firebase Authentication record) deleted within 30 days
- Feedback records associated with the uid deleted within 30 days
- Survey records in the surveys collection are not automatically deleted on account deletion. Following deletion of the associated account record, survey records remain linked only to a Firebase-generated technical identifier (uid). Zella will no longer be reasonably able to attribute those records to a specific individual using information available to it.
- Billing records retained 7 years under HMRC legal obligation (Article 17(3)(b) exemption)
- Aggregated team averages (teamAverages) are not personal data and are not subject to erasure

What happens to company data on account closure:
When a company account is closed, all human-readable company identifiers (company name, administrator names and emails, team names) are deleted within 30 days. Survey records and team averages retain the Firebase-generated company ID code — a long alphanumeric string with no inherent meaning. Because the company account record linking that code to a company name no longer exists, Zella will no longer be reasonably able to attribute remaining records to a specific organisation using information available to it.

Extended erasure on specific written request:
The Customer or individual employees may request additional deletion beyond the standard process by emailing privacy@zellawellbeing.com with subject "Extended Erasure Request." Zella will:
- Consider each request in good faith
- Delete survey records associated with specific uids within 30 days where technically feasible
- Provide a written response explaining what has been deleted and what, if anything, could not be deleted and why

Zella cannot guarantee deletion of all records in all circumstances, particularly where doing so would compromise the statistical integrity of research datasets derived from aggregated data, conflict with HMRC or other legal retention obligations, or require deletion of records that are no longer attributable to any identified individual.

Right to Rectification (Article 16):
Employees can update display name and password in the app. Email corrections: privacy@zellawellbeing.com.

Right to Restriction (Article 18):
Zella can suspend processing of a specific employee's data on written request from the Customer.

Right to Portability (Article 20):
Account data and survey records in JSON or CSV format, within 30 days of written request.

Right to Object (Article 21):
The Customer is responsible for assessing objections. Where upheld, the Customer should instruct Zella to restrict or delete the relevant data.

Process:
Email privacy@zellawellbeing.com with subject "Data Subject Rights Assistance — [right]" including the employee's uid or email address. Zella will respond within 30 days and may request reasonable verification of the Customer's authority.`
    },
    {
      title: "8. Personal Data Breach Notification",
      content: `Detection and Escalation:

Zella maintains incident response procedures for detecting and assessing potential personal data breaches. Suspected breaches are escalated internally and assessed for risk within 24 hours of detection.

Notification to Customer:

Zella will notify the Customer without undue delay and in any event within 48 hours of becoming aware of a personal data breach affecting data processed under this DPA.

The notification will include, to the extent available at the time:
- The nature of the breach, including categories and approximate number of affected data subjects and records
- Contact details of Zella's data protection contact
- The likely consequences of the breach
- Measures taken or proposed to address the breach and mitigate harm
- The time and date the breach was detected and, if known, when it occurred

Where full information is not available within 48 hours, Zella will provide an initial notification and supplement it as further details become known.

Notification to Supervisory Authority and Data Subjects:

The Customer, as data controller, is responsible for notifying the ICO under Article 33 and communicating with affected data subjects under Article 34 where required. Zella will provide all reasonable assistance.

Security Incidents That Are Not Breaches:

Zella will also inform the Customer of significant security incidents that do not constitute a personal data breach but may be relevant to the Customer's risk assessment.

Breach Records:

Zella maintains an internal record of all personal data breaches, available to the Customer on audit request.`
    },
    {
      title: "9. Data Deletion and Return",
      content: `On Termination — Customer's Election:

On termination of the Customer's account, the Customer may elect to:
- Receive an export of personal data (employee account records and survey records in JSON or CSV format) by requesting this from privacy@zellawellbeing.com before account closure; or
- Proceed with deletion only (the default)

If no instruction is received within 30 days of account termination, Zella will proceed with deletion.

Standard Deletion on Account Closure:

Employee and administrator account data (users collection, admins collection, Firebase Authentication records): Deleted within 30 days of account closure.

Company and team records (companies collection, teams collection): Deleted within 30 days. This includes all human-readable identifiers — company name, team names, administrator names and contact details.

Feedback records: Deleted within 30 days.

Rate limiting records: Automatically deleted within 48 hours (this is ongoing, not triggered by account closure).

Survey records (surveys collection): Survey records contain Firebase-generated identifier codes (uid, teamId, companyId) — not human-readable names. On account closure, the company account record linking the companyId code to the company name is deleted. Survey records remain in the database but retain only the technical identifier codes. Following deletion of the company account record, Zella will no longer be reasonably able to attribute those survey records to a specific organisation using information available to it. The companyId code in retained survey records is a long alphanumeric Firebase-generated string with no inherent meaning absent the account record.

Team averages (teamAverages collection): Same position as survey records — company and team name records are deleted; teamAverage records retain only Firebase-generated ID codes and aggregate numerical values.

Billing records: Retained for 7 years from the date of each transaction (HMRC legal obligation — Article 17(3)(b) exemption applies).

Extended Deletion on Request:

The Customer may request additional deletion beyond the standard process — for example, deletion of all survey records whose companyId matches the Customer's company — by emailing privacy@zellawellbeing.com with subject "Extended Deletion Request." Zella will consider each request in good faith and respond within 30 days, explaining what has been deleted and, where full deletion is not possible, the reason why (for example, where deletion would compromise the integrity of aggregated research datasets or conflict with legal retention obligations).

Confirmation of Deletion:

Zella will provide written confirmation to the Customer on request that deletion has been completed, within 30 days of completion.

Force Majeure:

Neither party is liable for delays in deletion or return caused by circumstances beyond reasonable control, provided reasonable steps are taken to minimise delay.`
    },
    {
      title: "10. Audit Rights and Demonstration of Compliance",
      content: `Information Requests:

The Customer may at any time request written information from Zella to demonstrate compliance with this DPA, including:
- Copies of relevant security policies and procedures
- Copies of Firestore security rules
- Copies of sub-processor agreements (redacted for commercial confidentiality where necessary)
- Data flow diagrams
- Article 30(2) records of processing activities
- Records of personal data breaches

All information provided is subject to confidentiality obligations and must not be disclosed to third parties without Zella's consent, to the extent it contains proprietary or commercially sensitive information.

Zella will respond to information requests within 30 days.

Formal Audit Rights:

The Customer may, with at least 30 days written notice, request a formal audit of Zella's data processing activities relevant to this DPA. Formal audits may be conducted no more than once per calendar year, except following a personal data breach or regulatory investigation.

Formal audits are subject to:
- Confidentiality protections for Zella's proprietary, commercially sensitive, and security-sensitive information
- Reasonable restrictions to protect the security of the platform and other customers' data
- A mutually agreed scope and methodology

Zella may satisfy a formal audit through written documentation, a structured questionnaire, or facilitation of a remote or on-site third-party audit by a mutually agreed independent auditor.

The Customer is responsible for all costs associated with formal audits, including reasonable Zella staff time.

Regulatory Cooperation:

Zella will cooperate fully with any audit, investigation, or request from the ICO, any EU supervisory authority, or other competent regulatory authority, and will promptly notify the Customer of any such request relating to data processed under this DPA.`
    },
    {
      title: "11. Liability and Indemnification",
      content: `Allocation of Liability under Data Protection Legislation:

Each party is liable for damage caused to a data subject by processing that infringes Data Protection Legislation where that party is responsible, in accordance with Article 82 of UK GDPR. If both parties are responsible for the same damage, each may be held liable for the entire amount in order to ensure effective compensation for the data subject, with contribution between parties in proportion to their respective responsibility.

Liability Cap — Business Customers:

Zella's total liability to the Customer under or in connection with this DPA shall not exceed the greater of:
- The total fees paid by the Customer to Zella in the 12 months immediately preceding the event giving rise to the claim; or
- £5,000 (five thousand pounds)

This cap applies to liability to the Customer. It does not limit Zella's liability to data subjects under Article 82 of UK GDPR.

Exclusions from Liability Cap:

The following are not subject to the liability cap:
- Death or personal injury caused by Zella's negligence
- Fraud or fraudulent misrepresentation by Zella
- Any liability that cannot be excluded or limited by applicable law

Customer Indemnity:

The Customer agrees to indemnify, defend, and hold harmless Zella and its officers, directors, employees, and agents from and against all claims, losses, damages, penalties, fines, costs, and expenses (including reasonable legal fees) arising from or related to:
- The Customer's failure to comply with its obligations as data controller under Data Protection Legislation
- The Customer's failure to provide employees with adequate transparency about Zella's use
- The Customer's deployment or use of Zella in a manner that violates the Terms of Service or this DPA
- Any fine or penalty imposed on Zella by the ICO or any supervisory authority arising from the Customer's acts or omissions as data controller
- Any third-party claim (including by an employee) arising from the Customer's misuse of Zella data or employment decisions made using Zella data

Zella's Indemnity:

Zella agrees to indemnify the Customer against claims, fines, and losses arising directly and solely from Zella's material breach of its obligations as data processor under this DPA.`
    },
    {
      title: "12. Term, Termination, and Survival",
      content: `Commencement:

This DPA takes effect on the date the Customer accepts the Terms of Service and continues for as long as Zella processes personal data on behalf of the Customer.

Termination:

This DPA terminates automatically on the effective date of termination of the Customer's Zella account.

Effect of Termination:

On termination, Zella will cease processing personal data on behalf of the Customer and will delete or return data as set out in Section 9.

Survival:

The following provisions survive termination:
- Section 5(b) (Confidentiality) — indefinitely
- Section 8 (Breach Notification) — for breaches discovered after termination relating to data processed during the term
- Section 9 (Data Deletion and Return) — until obligations are completed
- Section 10 (Audit Rights) — for 12 months following termination
- Section 11 (Liability and Indemnification) — indefinitely
- Section 12 (Term, Termination, and Survival) — indefinitely
- Section 13 (Governing Law) — indefinitely`
    },
    {
      title: "13. Governing Law and Jurisdiction",
      content: `This DPA is governed by the laws of England and Wales. Any dispute arising out of or in connection with this DPA shall be subject to the exclusive jurisdiction of the courts of England and Wales, except that either party may seek urgent injunctive or equitable relief in any court of competent jurisdiction.

EU Customers:

Where the Customer is established in an EU member state, this DPA shall also be interpreted in accordance with EU GDPR to the extent that EU GDPR applies to the processing of personal data of EU residents.

Language:

This DPA is written in English. If translated, the English version prevails.`
    },
    {
      title: "14. Amendments",
      content: `Zella may amend this DPA to reflect changes in Data Protection Legislation, processing activities, or sub-processors.

Material amendments will be notified to the Customer by email at least 30 days before taking effect. Continued use of Zella after the notice period constitutes acceptance.

If the Customer objects to a material amendment on reasonable data protection grounds, the Customer may terminate the Services on written notice within the 30-day period without penalty.

Amendments required immediately by law take effect immediately, with notification to the Customer as soon as reasonably practicable.`
    },
    {
      title: "15. Contact and Data Protection Enquiries",
      content: `All data protection and DPA-related enquiries:

Email: privacy@zellawellbeing.com

Suggested subject lines:
- "DPA Enquiry" — general questions
- "Data Subject Rights Assistance — [right]" — rights request assistance
- "Sub-Processor Change Objection" — to object to a notified change
- "Audit Request" — to initiate a formal audit
- "Extended Erasure Request" — to request deletion beyond the standard process
- "Extended Deletion Request" — for company-level extended deletion on account closure
- "EU Transfer Mechanism Request" — to request SCC documentation
- "URGENT — Data Breach" — to report a suspected breach

Postal address:
Ursell Property Limited (trading as Zella Fatigue & Wellbeing)
Carpenters House, 24 The Street, Leighterton, GL8 8UN
Company number: 11056504

Data Protection Officer: Not appointed.

Response times:
General DPA enquiries: 5 business days
Data subject rights and deletion requests: 30 days
Urgent breach notifications: 48 hours`
    }
  ];

  const summary = {
    title: "Plain Language Summary",
    content: `This Data Processing Agreement (DPA) is a legally required contract under UK GDPR Article 28.

The two roles:
You (the Customer) are the data controller — you decide why and how employee data is collected.
Zella is the data processor — we process it on your behalf, strictly as instructed.

What Zella processes:
- Employee account data: email, name, team and company assignment (stored as Firebase-generated ID codes)
- Survey responses: happiness (1–5), tiredness (1–5), optional reaction time — stored with each employee's Firebase-generated user ID
- Feedback and bug reports: stored with employee user ID
- Weekly team averages: aggregate scores, no individual identifiers

Important — pseudonymisation not anonymisation:
Survey records contain each employee's user ID. Administrators cannot access these records directly through the dashboard — they see only team averages. The user ID links to the account record, which is deleted when an employee deletes their account. After account deletion, records remain linked only to a technical identifier and Zella can no longer reasonably attribute them to a specific individual.

What happens when your company account is closed:
All human-readable company and employee data is deleted within 30 days. Survey records and team averages retain only Firebase-generated ID codes — long alphanumeric strings with no inherent meaning once the account record linking them to your company no longer exists.

You can request extended deletion (e.g. deletion of all records matching your company ID) by emailing privacy@zellawellbeing.com. Zella will consider this in good faith. We cannot guarantee every request can be fulfilled — particularly where doing so would compromise research dataset integrity or conflict with legal retention obligations.

Your obligations as data controller:
- Have a lawful basis under UK GDPR for collecting employee data through Zella
- Tell your employees you are using Zella and what data is collected
- Respond to employee data rights requests (Zella will assist)
- Ensure compliance with employment law on workplace monitoring
- Not use Zella outputs as the sole basis for individual employment decisions

Sub-processors:
- Firebase / Google Cloud (UK, London) — all application data
- Stripe (US, UK-US Data Bridge) — payments only, no employee data
- Google reCAPTCHA (US, UK-US Data Bridge) — registration bot prevention only

Billing records: retained 7 years (HMRC requirement).
Liability cap: greater of fees paid in prior 12 months or £5,000.

Questions? Email privacy@zellawellbeing.com`
  };

  return (
    <TermsLayout>
      <h1>Data Processing Agreement</h1>
      <p className="last-updated">Last Updated: June 2026</p>

      <div style={{ backgroundColor: '#fff8e1', border: '1px solid #ffa405', borderRadius: '8px', padding: '16px', marginBottom: '32px' }}>
        <p style={{ margin: 0, fontSize: '14px', color: '#555' }}>
          <strong>Note:</strong> This Data Processing Agreement forms part of Zella's Terms of Service and is accepted at the point of account creation. No separate signature is required. This agreement is required by UK GDPR Article 28 and sets out the legally mandated terms governing Zella's processing of your employees' personal data on your behalf.
        </p>
      </div>

      {sections.map((section, index) => (
        <div key={index} style={{ marginBottom: '40px' }}>
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginTop: '30px', marginBottom: '15px', color: '#333' }}>
            {section.title}
          </h2>
          <div style={{ whiteSpace: 'pre-line', lineHeight: '1.8', fontSize: '15px', color: '#555' }}>
            {section.content}
          </div>
        </div>
      ))}

      <div style={{ backgroundColor: '#f8f9fa', border: '2px solid #1ab02a', borderRadius: '8px', padding: '24px', marginTop: '40px', marginBottom: '40px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '15px', color: '#032054' }}>
          {summary.title}
        </h2>
        <div style={{ whiteSpace: 'pre-line', lineHeight: '1.8', fontSize: '15px', color: '#333' }}>
          {summary.content}
        </div>
      </div>

      <div style={{ borderTop: '2px solid #dee2e6', paddingTop: '20px', marginTop: '40px', fontSize: '14px', color: '#6c757d' }}>
        <p><strong>Version: 3.0</strong></p>
        <p><strong>Governing Law: England and Wales</strong></p>
        <p><strong>Required by: UK GDPR Article 28 / EU GDPR Article 28</strong></p>
        <p style={{ marginTop: '20px', fontSize: '16px', fontWeight: 'bold', color: '#333' }}>Zella — Workplace Wellbeing Monitoring</p>
        <p>This DPA forms part of Zella's Terms of Service and is binding on all Customer organisations.</p>
      </div>
    </TermsLayout>
  );
}

export default DataProcessingAgreement;